import { useState } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';

// Swipeable row component for mobile interactions
// Swipe left to delete, swipe right to edit
// Optimized for iOS and Android touch handling
export function SwipeableRow({ 
  children, 
  onSwipeLeft, 
  onSwipeRight,
  leftLabel = 'Delete',
  rightLabel = 'Paid',
  leftColor = '#EF4444',
  rightColor = '#10B981',
  disabled = false
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const x = useMotionValue(0);
  
  // Transform x position to background opacity
  const leftBgOpacity = useTransform(x, [-150, -50, 0], [1, 0.5, 0]);
  const rightBgOpacity = useTransform(x, [0, 50, 150], [0, 0.5, 1]);
  
  const handleDragEnd = (event, info) => {
    const threshold = 80; // Lower threshold for easier triggering on mobile
    
    if (info.offset.x < -threshold && onSwipeLeft) {
      setIsDeleting(true);
      setTimeout(() => {
        onSwipeLeft();
      }, 200);
    } else if (info.offset.x > threshold && onSwipeRight) {
      onSwipeRight();
    }
  };

  if (disabled) {
    return <div>{children}</div>;
  }

  return (
    <div className="relative overflow-hidden rounded-xl select-none">
      {/* Background actions */}
      <motion.div 
        className="absolute inset-0 flex items-center justify-start px-4 rounded-xl"
        style={{ 
          backgroundColor: rightColor,
          opacity: rightBgOpacity
        }}
      >
        <span className="text-white font-bold text-sm flex items-center gap-2">
          ✓ {rightLabel}
        </span>
      </motion.div>
      
      <motion.div 
        className="absolute inset-0 flex items-center justify-end px-4 rounded-xl"
        style={{ 
          backgroundColor: leftColor,
          opacity: leftBgOpacity
        }}
      >
        <span className="text-white font-bold text-sm flex items-center gap-2">
          {leftLabel} 🗑️
        </span>
      </motion.div>
      
      {/* Draggable content - optimized for mobile touch */}
      <AnimatePresence>
        {!isDeleting && (
          <motion.div
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragEnd={handleDragEnd}
            style={{ x }}
            initial={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white cursor-grab active:cursor-grabbing"
            // Touch optimization for iOS/Android
            whileTap={{ cursor: 'grabbing' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SwipeableRow;
